import { useState, useEffect, useRef, useCallback } from 'react'

export interface DraggablePosition {
  x: number
  y: number
}

interface UseDraggableOptions {
  /** Clave en localStorage para persistir posición (opcional) */
  storageKey?: string
  /** Posición inicial si no hay persistencia */
  defaultPosition: DraggablePosition
  /** Margen en px desde los bordes del viewport (default: 8) */
  margin?: number
  /** Umbral en px para distinguir click de drag (default: 5) */
  dragThreshold?: number
  /** Tamaño del elemento (para clamp correcto) */
  size: { width: number; height: number }
  /** Si 'right', fija X al borde derecho del viewport: el elemento solo se arrastra en vertical */
  pinX?: 'right'
}

interface UseDraggableReturn {
  position: DraggablePosition
  isDragging: boolean
  /** Handler para pasar al elemento que se arrastra */
  handlers: {
    onPointerDown: (e: React.PointerEvent<HTMLElement>) => void
  }
  /** true si el último pointerup fue realmente un drag (no un click) */
  wasDragged: boolean
  /** Reset de la flag wasDragged. Llamar después de manejar el click. */
  resetWasDragged: () => void
}

/**
 * Hook para hacer un elemento arrastrable con pointer events (mouse + touch).
 *
 * - Persiste posición en localStorage (opcional)
 * - Clamp al viewport con margen configurable
 * - Re-clamp automático al redimensionar la window
 * - Umbral de drag para distinguir click vs. arrastre (permite mantener onClick)
 *
 * @example
 * ```tsx
 * const { position, handlers, wasDragged, resetWasDragged } = useDraggable({
 *   storageKey: 'my-app:floating-btn',
 *   defaultPosition: { x: 20, y: 20 },
 *   size: { width: 48, height: 48 },
 * })
 *
 * const handleClick = () => {
 *   if (wasDragged) { resetWasDragged(); return }
 *   // ... lógica de click
 * }
 *
 * return (
 *   <button
 *     style={{ position: 'fixed', left: position.x, top: position.y, touchAction: 'none' }}
 *     onPointerDown={handlers.onPointerDown}
 *     onClick={handleClick}
 *   >...</button>
 * )
 * ```
 */
export function useDraggable({
  storageKey,
  defaultPosition,
  margin = 8,
  dragThreshold = 5,
  size,
  pinX,
}: UseDraggableOptions): UseDraggableReturn {
  const [position, setPosition] = useState<DraggablePosition>(() => {
    if (typeof window === 'undefined') return defaultPosition
    if (storageKey) {
      try {
        const stored = window.localStorage.getItem(storageKey)
        if (stored) {
          const parsed = JSON.parse(stored) as DraggablePosition
          if (
            typeof parsed?.x === 'number' &&
            typeof parsed?.y === 'number' &&
            Number.isFinite(parsed.x) &&
            Number.isFinite(parsed.y)
          ) {
            return clampToViewport(parsed, size, margin, pinX)
          }
        }
      } catch {
        // ignore parse/storage errors
      }
    }
    return clampToViewport(defaultPosition, size, margin, pinX)
  })

  const [isDragging, setIsDragging] = useState(false)
  const [wasDragged, setWasDragged] = useState(false)

  const dragStartRef = useRef<{
    pointerX: number
    pointerY: number
    initialX: number
    initialY: number
    moved: boolean
  } | null>(null)

  // Persist a localStorage cuando la posición cambia (solo si hay storageKey)
  useEffect(() => {
    if (!storageKey) return
    try {
      window.localStorage.setItem(storageKey, JSON.stringify(position))
    } catch {
      // quota exceeded / disabled — ignorar
    }
  }, [position, storageKey])

  // Re-clamp en resize de la ventana
  useEffect(() => {
    const handleResize = () => {
      setPosition((prev) => clampToViewport(prev, size, margin, pinX))
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
    // size es un objeto; lo serializamos para evitar loops en deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [size.width, size.height, margin, pinX])

  const onPointerDown = useCallback(
    (e: React.PointerEvent<HTMLElement>) => {
      // No arrastrar con botones secundarios (ej. menú contextual)
      if (e.button !== 0 && e.pointerType === 'mouse') return

      e.preventDefault()
      const target = e.currentTarget
      try {
        target.setPointerCapture(e.pointerId)
      } catch {
        // algunas plataformas fallan al capturar — continuar
      }

      dragStartRef.current = {
        pointerX: e.clientX,
        pointerY: e.clientY,
        initialX: position.x,
        initialY: position.y,
        moved: false,
      }
      setIsDragging(true)

      const handlePointerMove = (moveEvent: PointerEvent) => {
        if (!dragStartRef.current) return
        const dx = moveEvent.clientX - dragStartRef.current.pointerX
        const dy = moveEvent.clientY - dragStartRef.current.pointerY

        if (!dragStartRef.current.moved && Math.hypot(dx, dy) >= dragThreshold) {
          dragStartRef.current.moved = true
        }

        if (dragStartRef.current.moved) {
          const next = clampToViewport(
            { x: dragStartRef.current.initialX + dx, y: dragStartRef.current.initialY + dy },
            size,
            margin,
            pinX
          )
          setPosition(next)
        }
      }

      const handlePointerEnd = () => {
        const moved = dragStartRef.current?.moved ?? false
        if (moved) setWasDragged(true)
        setIsDragging(false)
        dragStartRef.current = null
        target.removeEventListener('pointermove', handlePointerMove)
        target.removeEventListener('pointerup', handlePointerEnd)
        target.removeEventListener('pointercancel', handlePointerEnd)
        try {
          target.releasePointerCapture(e.pointerId)
        } catch {
          // ignorar
        }
      }

      target.addEventListener('pointermove', handlePointerMove)
      target.addEventListener('pointerup', handlePointerEnd)
      target.addEventListener('pointercancel', handlePointerEnd)
    },
    [position.x, position.y, size, margin, dragThreshold, pinX]
  )

  const resetWasDragged = useCallback(() => setWasDragged(false), [])

  return {
    position,
    isDragging,
    handlers: { onPointerDown },
    wasDragged,
    resetWasDragged,
  }
}

function clampToViewport(
  pos: DraggablePosition,
  size: { width: number; height: number },
  margin: number,
  pinX?: 'right'
): DraggablePosition {
  if (typeof window === 'undefined') return pos
  const maxX = Math.max(margin, window.innerWidth - size.width - margin)
  const maxY = Math.max(margin, window.innerHeight - size.height - margin)
  return {
    x: pinX === 'right' ? maxX : Math.max(margin, Math.min(pos.x, maxX)),
    y: Math.max(margin, Math.min(pos.y, maxY)),
  }
}
